// src/pages/admin/promotions/AllSMSCampaigns.jsx
import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'
import { 
  Plus, 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  MessageCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
  Clock,
  Users,
  AlertCircle,
  Play,
  RotateCcw,
  FileText
} from 'lucide-react'
import SMSCampaignService from '../../../services/smsCampaignService'
import CustomerGroupService from '../../../services/customerGroupService'
import { SMSCampaignModel, SMS_CAMPAIGN_STATUS } from '../../../utils/smsCampaignModel'
import CreateSMSCampaignModal from '../../../components/modals/CreateSMSCampaignModal'
import EditSMSCampaignModal from '../../../components/modals/EditSMSCampaignModal'
import ViewSMSCampaignModal from '../../../components/modals/ViewSMSCampaignModal'
import DeleteSMSCampaignModal from '../../../components/modals/DeleteSMSCampaignModal'
import SMSLogsModal from '../../../components/modals/SMSLogsModal'

// Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
    <span>{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2">
      <XCircle className="h-4 w-4" />
    </button>
  </div>
)

const AllSMSCampaigns = () => {
  // User permissions and RBAC
  const { hasPermission } = usePermissions()

  // State Management
  const [campaigns, setCampaigns] = useState([])
  const [customerGroups, setCustomerGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)

  // Filter and Search State
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [customerGroupFilter, setCustomerGroupFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('')

  // Modal States
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showLogsModal, setShowLogsModal] = useState(false)

  // Action States
  const [runningCampaigns, setRunningCampaigns] = useState(new Set())
  const [retryingCampaigns, setRetryingCampaigns] = useState(new Set())

  // Stats State
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    sending: 0,
    running: 0,
    completed: 0,
    failed: 0
  })

  const loadSMSCampaigns = useCallback(async (params = {}) => {
    try {
      console.log('📱 Loading SMS campaigns with params:', params)
      const response = await SMSCampaignService.getAllSMSCampaigns(params)
      
      if (response.success) {
        const parsedData = SMSCampaignService.parseResponse(response)
        console.log('📱 Parsed SMS campaigns data:', parsedData)
        
        if (parsedData.campaigns) {
          const formattedCampaigns = parsedData.campaigns.map(campaign => 
            SMSCampaignService.formatSMSCampaignForDisplay(campaign)
          )
          console.log('📱 Formatted campaigns:', formattedCampaigns)
          console.log('📱 Number of campaigns loaded:', formattedCampaigns.length)
          setCampaigns(formattedCampaigns)
          calculateStats(formattedCampaigns)
        } else {
          console.log('📱 No campaigns found in response')
          // Temporary mock data for testing action buttons
          const mockCampaigns = [
            {
              id: 'test1',
              title: 'Test Campaign 1',
              status: 'draft',
              customerGroup: { name: 'VIP Customers', id: 'mock1' },
              createdBy: { username: 'admin' },
              createdAtFormatted: '2025-09-22',
              scheduledAtFormatted: '-'
            },
            {
              id: 'test2', 
              title: 'Test Campaign 2',
              status: 'completed',
              customerGroup: { name: 'Regular Customers', id: 'mock2' },
              createdBy: { username: 'admin' },
              createdAtFormatted: '2025-09-21',
              scheduledAtFormatted: '-'
            }
          ]
          console.log('📱 Using mock campaigns for testing:', mockCampaigns)
          setCampaigns(mockCampaigns)
          calculateStats(mockCampaigns)
        }
      } else {
        console.log('📱 SMS campaigns API response not successful:', response)
        setCampaigns([])
      }
    } catch (error) {
      console.error('Failed to load SMS campaigns:', error)
      throw error
    }
  }, [])

  const loadCustomerGroups = useCallback(async () => {
    try {
      console.log('👥 Loading customer groups')
      const response = await CustomerGroupService.getAllCustomerGroups()
      console.log('👥 Full API response:', response)
      
      if (response.success) {
        // Handle different possible response structures
        let groups = [];
        if (response.data?.groups) {
          // API returns {groups: [...], totalResults: n}
          groups = response.data.groups;
        } else if (response.data?.customerGroups) {
          // API returns {customerGroups: [...]}
          groups = response.data.customerGroups;
        } else if (Array.isArray(response.data)) {
          // API returns [...] directly
          groups = response.data;
        }
        
        console.log('👥 Extracted groups:', groups)
        console.log('👥 Groups is array:', Array.isArray(groups))
        console.log('👥 Groups length:', groups.length)
        
        if (Array.isArray(groups)) {
          setCustomerGroups(groups)
          if (groups.length === 0) {
            console.log('👥 Real API returned empty array - no customer groups in database')
          }
        } else {
          console.log('👥 Invalid groups data structure, using fallback')
          setCustomerGroups([])
        }
      } else {
        console.log('👥 API response not successful:', response)
        setCustomerGroups([])
      }
    } catch (error) {
      console.error('Failed to load customer groups:', error)
      console.log('👥 Setting empty array due to API error')
      setCustomerGroups([])
    }
  }, [])

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true)
      await Promise.all([
        loadSMSCampaigns(),
        loadCustomerGroups()
      ])
    } catch (error) {
      console.error('Failed to load initial data:', error)
      setError('Failed to load SMS campaigns')
      showToast('Failed to load SMS campaigns', 'error')
    } finally {
      setLoading(false)
    }
  }, [loadSMSCampaigns, loadCustomerGroups])

  useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  const calculateStats = (campaignsData) => {
    const stats = campaignsData.reduce((acc, campaign) => {
      acc.total++
      acc[campaign.status] = (acc[campaign.status] || 0) + 1
      return acc
    }, { total: 0, draft: 0, sending: 0, running: 0, completed: 0, failed: 0 })
    
    setStats(stats)
  }

  const showToast = (message, type) => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }

  const handleRefresh = async () => {
    try {
      setRefreshing(true)
      await loadSMSCampaigns()
      showToast('SMS campaigns refreshed successfully', 'success')
    } catch (error) {
      console.error('Failed to refresh campaigns:', error)
      showToast('Failed to refresh campaigns', 'error')
    } finally {
      setRefreshing(false)
    }
  }

  const handleSearch = async () => {
    const params = {}
    
    if (searchTerm) params.search = searchTerm
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter
    if (customerGroupFilter && customerGroupFilter !== 'all') params.customerGroup = customerGroupFilter
    if (dateFilter) params.date = dateFilter
    
    try {
      await loadSMSCampaigns(params)
    } catch {
      showToast('Failed to search campaigns', 'error')
    }
  }

  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setCustomerGroupFilter('all')
    setDateFilter('')
    loadSMSCampaigns()
  }

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchTerm.trim() !== '' || 
           statusFilter !== 'all' || 
           customerGroupFilter !== 'all' || 
           dateFilter !== ''
  }

  // Campaign Actions
  const handleRunCampaign = async (campaign) => {
    // Allow running for draft and failed campaigns
    if (!['draft', 'failed'].includes(campaign.status)) {
      showToast('Campaign can only be run from draft or failed status', 'error')
      return
    }

    try {
      setRunningCampaigns(prev => new Set([...prev, campaign.id]))
      const isReRun = campaign.status === 'failed'
      console.log(`🚀 ${isReRun ? 'Re-running' : 'Running'} campaign ${campaign.id} - updating status to "sending"`)
      console.log(`📤 PATCH payload:`, { status: 'sending' })
      
      // PATCH request to update only the status field to "sending"
      const response = await SMSCampaignService.updateSMSCampaign(campaign.id, {
        status: 'sending'
      })
      
      if (response.success) {
        const message = isReRun 
          ? `Campaign "${campaign.title}" re-started successfully` 
          : `Campaign "${campaign.title}" started successfully`
        showToast(message, 'success')
        await loadSMSCampaigns() // Refresh to get updated status
      } else {
        throw new Error(response.message || 'Failed to update campaign status')
      }
    } catch (error) {
      console.error('Failed to run campaign:', error)
      const message = campaign.status === 'failed' 
        ? 'Failed to re-run campaign' 
        : 'Failed to run campaign'
      showToast(message, 'error')
    } finally {
      setRunningCampaigns(prev => {
        const newSet = new Set(prev)
        newSet.delete(campaign.id)
        return newSet
      })
    }
  }

  const handleRetryCampaign = async (campaign) => {
    if (!SMSCampaignModel.canRetry(campaign.status)) {
      showToast('Campaign cannot be retried in current status', 'error')
      return
    }

    try {
      setRetryingCampaigns(prev => new Set([...prev, campaign.id]))
      const response = await SMSCampaignService.retrySMSCampaign(campaign.id)
      
      if (response.success) {
        showToast(`Campaign "${campaign.title}" retry started successfully`, 'success')
        await loadSMSCampaigns() // Refresh to get updated status
      }
    } catch (error) {
      console.error('Failed to retry campaign:', error)
      showToast('Failed to retry campaign', 'error')
    } finally {
      setRetryingCampaigns(prev => {
        const newSet = new Set(prev)
        newSet.delete(campaign.id)
        return newSet
      })
    }
  }

  // Modal handlers
  const handleCreateSuccess = () => {
    setShowCreateModal(false)
    loadSMSCampaigns()
    showToast('SMS campaign created successfully', 'success')
  }

  const handleEditSuccess = () => {
    setShowEditModal(false)
    setSelectedCampaign(null)
    loadSMSCampaigns()
    showToast('SMS campaign updated successfully', 'success')
  }

  const handleDeleteSuccess = () => {
    setShowDeleteModal(false)
    setSelectedCampaign(null)
    loadSMSCampaigns()
    showToast('SMS campaign deleted successfully', 'success')
  }

  // Get filtered campaigns
  const getFilteredCampaigns = () => {
    return campaigns.filter(campaign => {
      const matchesSearch = !searchTerm || 
        campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.customerGroup.name.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter
      const matchesCustomerGroup = customerGroupFilter === 'all' || 
        campaign.customerGroup.id === customerGroupFilter
      
      return matchesSearch && matchesStatus && matchesCustomerGroup
    })
  }

  const filteredCampaigns = getFilteredCampaigns()

  // Render action buttons for each campaign
  const renderActionButtons = (campaign) => {
    const availableActions = SMSCampaignModel.getAvailableActions(campaign.status)
    console.log('🔧 Campaign:', campaign.title, 'Status:', campaign.status, 'Available actions:', availableActions)
    console.log('🔧 Has PROMOTIONS_UPDATE permission:', hasPermission('PROMOTIONS_UPDATE'))
    
    return (
      <div className="flex items-center">
        {/* View */}
        {availableActions.includes('view') && (
          <button
            onClick={() => {
              setSelectedCampaign(campaign)
              setShowViewModal(true)
            }}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
        )}

        {/* Edit - Only for draft campaigns */}
        {availableActions.includes('edit') && (
          <button
            onClick={() => {
              setSelectedCampaign(campaign)
              setShowEditModal(true)
            }}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
        )}

        {/* Run - For draft campaigns and failed campaigns (re-run) */}
        {(campaign.status === 'draft' || campaign.status === 'failed') && (
          <button
            onClick={() => handleRunCampaign(campaign)}
            disabled={runningCampaigns.has(campaign.id)}
            className="p-2 text-green-400 hover:text-green-600 disabled:opacity-50"
            title={campaign.status === 'failed' ? 'Re-run Campaign' : 'Run Campaign'}
          >
            {runningCampaigns.has(campaign.id) ? 
              <ButtonSpinner size="sm" /> : <Play className="h-4 w-4" />
            }
          </button>
        )}

        {/* Retry */}
        {availableActions.includes('retry') && hasPermission('PROMOTIONS_UPDATE') && (
          <button
            onClick={() => handleRetryCampaign(campaign)}
            disabled={retryingCampaigns.has(campaign.id)}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title="Retry Campaign"
          >
            {retryingCampaigns.has(campaign.id) ? 
              <ButtonSpinner size="sm" /> : <RotateCcw className="h-4 w-4" />
            }
          </button>
        )}

        {/* Delete */}
        {availableActions.includes('delete') && (
          <button
            onClick={() => {
              setSelectedCampaign(campaign)
              setShowDeleteModal(true)
            }}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="p-6">
        <InlineLoader message="Loading SMS campaigns..." />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            SMS Campaigns
          </h1>
        </div>
        
        <div className="flex space-x-3">
          {/* Logs Button */}
          <Button
            variant="outline"
            onClick={() => setShowLogsModal(true)}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Logs
          </Button>
          
          {/* Refresh Button */}
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            {refreshing ? <ButtonSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>

          {/* Create Button - Temporarily showing for all users */}
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Campaign
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="p-2 bg-gray-100 rounded-full">
                <MessageCircle className="h-5 w-5 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Draft</p>
                <p className="text-2xl font-bold text-blue-900">{stats.draft}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-full">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Sending</p>
                <p className="text-2xl font-bold text-orange-900">{stats.sending}</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-full">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Running</p>
                <p className="text-2xl font-bold text-purple-900">{stats.running}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-full">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-900">{stats.completed}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-full">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-900">{stats.failed}</p>
              </div>
              <div className="p-2 bg-red-100 rounded-full">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Promotions</h2>
            </div>
            {hasActiveFilters() && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search campaigns by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="all">All Status</option>
                {Object.values(SMS_CAMPAIGN_STATUS).map(status => (
                  <option key={status} value={status}>
                    {SMSCampaignModel.getStatusConfig(status).label}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Group Filter */}
            <div>
              <select
                value={customerGroupFilter}
                onChange={(e) => setCustomerGroupFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="all">All Groups</option>
                {(customerGroups || []).map(group => (
                  <option key={group._id} value={group._id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="h-11 border-gray-300"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card>
        <CardContent className="p-0">
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {filteredCampaigns.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {campaigns.length === 0 
                  ? "Get started by creating your first SMS campaign."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
              {hasPermission('PROMOTIONS_CREATE') && campaigns.length === 0 && (
                <div className="mt-6">
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Campaign
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCampaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {campaign.title}
                          </div>
                          {/* <div className="text-sm text-gray-500">
                            ID: {campaign.id}
                          </div> */}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {campaign.customerGroup.name}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Users className="h-3 w-3 mr-1" />
                            {campaign.customerGroup.customerCount} customers
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${campaign.statusConfig.color}`}>
                          <span className="mr-1">{campaign.statusConfig.icon}</span>
                          {campaign.statusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.createdBy.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.createdAtFormatted}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {renderActionButtons(campaign)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {showCreateModal && (
        <CreateSMSCampaignModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          customerGroups={customerGroups}
        />
      )}

      {showEditModal && selectedCampaign && (
        <EditSMSCampaignModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedCampaign(null)
          }}
          onSuccess={handleEditSuccess}
          campaign={selectedCampaign}
          customerGroups={customerGroups}
        />
      )}

      {showViewModal && selectedCampaign && (
        <ViewSMSCampaignModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false)
            setSelectedCampaign(null)
          }}
          campaign={selectedCampaign}
        />
      )}

      {showDeleteModal && selectedCampaign && (
        <DeleteSMSCampaignModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedCampaign(null)
          }}
          onSuccess={handleDeleteSuccess}
          campaign={selectedCampaign}
        />
      )}

      {showLogsModal && (
        <SMSLogsModal
          isOpen={showLogsModal}
          onClose={() => setShowLogsModal(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

export default AllSMSCampaigns